import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Collapse from '@mui/material/Collapse';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import { ToggleRenewalConfirmDialog } from '../TurnOffAutoRenew/ToggleRenewalConfirmDialog';
import { Panel } from '../../../../components/common/Panel';
import { AddOnsView } from './AddOnsView';
import DateTime from '../../../../utils/DateTime';
import {
  Feature,
  getProductId,
  isTestCloudFeature,
  PlanInterval,
  SecondarySource,
  Subscription,
  SubscriptionSource,
} from '../../../../models';
import { ToggleRenewal } from '../TurnOffAutoRenew/ToggleRenewal';
import { plan } from '../../../../layout/routes';
import { useNavigate, useQuery } from '../../../../routes';
import { fromSubscription, useAppDispatch } from '../../../../store';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { TrackingPayload } from '../../../../utils/utils';
import { isTrialSubscription } from '../../subscriptionTrial';
import { PLAN_SUBSCRIPTION_ID_QUERY_PARAM } from '../../../../utils/Constants';
import { isSelfServePaidSubscription } from '../../../../utils/subscriptionTarget';
import getFeaturePrefix = Feature.getFeaturePrefix;

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: 20,
    color: '#22283C',
  },
  contentGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    [theme.breakpoints.down('tablet')]: {
      flexDirection: 'column',
    },
  },
  subscriptionCard: {
    flex: 1,
    border: '1px solid #C4CAEC',
    borderRadius: 8,
    padding: theme.spacing(3),
    backgroundColor: '#fff',
  },
  planName: {
    fontWeight: 700,
    fontSize: 22,
    color: '#22283C',
  },
  planNameRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  seatsLabel: {
    fontWeight: 600,
    fontSize: 16,
    color: '#5959EB',
    marginTop: theme.spacing(0.5),
  },
  trialQuantity: {
    color: '#555B6C',
    marginLeft: theme.spacing(0.5),
  },
  trialBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #F5C451',
    borderRadius: 999,
    color: '#B76E00',
    backgroundColor: '#FFF8DB',
    fontWeight: 700,
    fontSize: 13,
    padding: theme.spacing(0.25, 1.5),
    marginRight: theme.spacing(1.5),
  },
  trialTitleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #F5C451',
    borderRadius: 999,
    color: '#B76E00',
    backgroundColor: '#FFF8DB',
    fontWeight: 700,
    fontSize: 13,
    padding: theme.spacing(0.25, 1.5),
  },
  trialSection: {
    borderTop: '1px solid #FDE68A',
    backgroundColor: '#FFFBEB',
    padding: theme.spacing(3.5),
  },
  trialHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  trialHeaderQuantity: {
    fontWeight: 700,
    fontSize: 18,
    color: '#22283C',
  },
  detailsSection: {
    borderTop: '1px solid #E0E3EF',
    backgroundColor: '#FAFAFF',
    padding: theme.spacing(2.5, 3.5),
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    '& + &': {
      borderTop: '1px solid #E0E3EF',
    },
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  detailQuantity: {
    fontWeight: 700,
    fontSize: 18,
    color: '#22283C',
  },
  managedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #C4CAEC',
    borderRadius: 999,
    color: '#4C5BAA',
    backgroundColor: '#F5F6FF',
    fontWeight: 700,
    fontSize: 12,
    padding: theme.spacing(0.25, 1.25),
    marginLeft: theme.spacing(1),
  },
  managedText: {
    color: '#727993',
    fontSize: 13,
  },
  addSeatsBtn: {
    marginTop: 'auto',
    alignSelf: 'flex-start',
    backgroundColor: '#5959EB',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
  renewalGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    flexWrap: 'nowrap',
    minWidth: 0,
  },
  renewalLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(0.5),
  },
  renewalDate: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283C',
  },
  renewalDays: {
    fontSize: 13,
    color: '#808B9A',
  },
  billingCycleValue: {
    fontWeight: 600,
    fontSize: 16,
    color: '#22283C',
  },
  autoRenewRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  autoRenewStatus: {
    fontSize: 14,
    fontWeight: 500,
  },
  viewMoreRow: {
    borderTop: '1px solid #E0E3EF',
    padding: theme.spacing(2.25),
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 16,
    width: '100%',
    borderRadius: 0,
    textAlign: 'center',
    textTransform: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#F8F8FF',
    },
  },
  viewMoreIcon: {
    marginRight: theme.spacing(1),
  },
  warningBanner: {
    backgroundColor: '#FFFBE6',
    border: '1px solid #FFD666',
    borderRadius: 6,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    margin: theme.spacing(0, 2, 2, 2),
  },
  warningText: {
    fontSize: 14,
    color: '#22283C',
  },
  reEnableLink: {
    color: '#5959EB',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  divider: {
    borderTop: '1px solid #E0E3EF',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
}));

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

const getTotalQuota = (subscriptions: Subscription[]) => (
  subscriptions.reduce((total, subscription) => total + (subscription.quota ?? 0), 0)
);

const getEarliestExpirySubscription = (subscriptions: Subscription[]) => (
  [...subscriptions].sort((first, second) => {
    const firstTime = first.expiryDate
      ? new Date(first.expiryDate).getTime()
      : Number.MAX_SAFE_INTEGER;
    const secondTime = second.expiryDate
      ? new Date(second.expiryDate).getTime()
      : Number.MAX_SAFE_INTEGER;
    return firstTime - secondTime;
  })[0]
);

const isPaidSubscription = (subscription: Subscription) => (
  !isTrialSubscription(subscription)
  && subscription.subscriptionType !== 'FREE'
);

export const TrueSubscription = (
  {
    trueSubscription,
    subscriptions,
    trackSubscriptionAction,
  }: {
    trueSubscription: Subscription,
    subscriptions?: Subscription[],
    trackSubscriptionAction?: (payload: TrackingPayload) => void,
  },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { flags } = useLaunchDarkly();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const groupedSubscriptions = subscriptions?.length ? subscriptions : [trueSubscription];
  const paidSubscriptions = groupedSubscriptions.filter(isPaidSubscription);
  const trialSubscriptions = groupedSubscriptions.filter(isTrialSubscription);
  const selfServePaidSubscriptions = paidSubscriptions.filter(isSelfServePaidSubscription);
  const selfServePaidSubscription = selfServePaidSubscriptions[0];
  const directSalePaidSubscriptions = paidSubscriptions.filter(
    subscription => subscription.secondarySource === SecondarySource.DIRECT_SALE,
  );
  const otherPaidSubscriptions = paidSubscriptions.filter(
    subscription => !isSelfServePaidSubscription(subscription)
      && subscription.secondarySource !== SecondarySource.DIRECT_SALE,
  );
  const displaySubscription = selfServePaidSubscription
    ?? paidSubscriptions[0]
    ?? trialSubscriptions[0]
    ?? trueSubscription;
  const detailPaidSubscriptions = [
    ...selfServePaidSubscriptions,
    ...otherPaidSubscriptions,
    ...directSalePaidSubscriptions,
  ].filter(subscription => subscription.id !== displaySubscription.id);
  const trialSubscription = getEarliestExpirySubscription(trialSubscriptions);
  const paidQuota = getTotalQuota(paidSubscriptions);
  const trialQuota = getTotalQuota(trialSubscriptions);
  const displayQuota = paidQuota + trialQuota || displaySubscription.quota;
  const hasPaidSubscription = paidQuota > 0;
  const hasTrialSubscription = trialQuota > 0;
  const isPrimaryTrial = !hasPaidSubscription && hasTrialSubscription;
  const hasDetailPaidSubscriptions = detailPaidSubscriptions.length > 0;
  const hasSubscriptionBreakdown = hasPaidSubscription
    && (hasDetailPaidSubscriptions || hasTrialSubscription);
  const canAddSeats = isSelfServePaidSubscription(displaySubscription)
    && getFeaturePrefix(displaySubscription.feature) !== 'Unknown';
  const isManagedByKatalon = displaySubscription.secondarySource === SecondarySource.DIRECT_SALE;
  const isAutoRenewEnabled = hasPaidSubscription
    && (displaySubscription?.autoRenewEnabled ?? false)
    && !displaySubscription?.canceledAt;
  const showAutoRenew = displaySubscription.source === SubscriptionSource.RECURLY
    && hasPaidSubscription;
  const [autoRenew, setAutoRenew] = useState(isAutoRenewEnabled);
  const [reEnableDialogOpen, setReEnableDialogOpen] = useState(false);
  const [showTrialDetails, setShowTrialDetails] = useState(true);

  useEffect(() => {
    setAutoRenew(isAutoRenewEnabled);
  }, [
    displaySubscription?.autoRenewEnabled,
    displaySubscription?.canceledAt,
    displaySubscription?.id,
    isAutoRenewEnabled,
  ]);

  const getQuotaUnitLabel = (feature: Feature, quota: number) => {
    if (isTestCloudFeature(feature)) {
      return quota === 1 ? 'session' : 'sessions';
    }
    if (
      feature === Feature.TRUE_TEAM
      || feature === Feature.TRUE_ENTERPRISE
      || feature === Feature.TRUE_PLATFORM_ONLY
      || feature === Feature.TRUE_AUTOMATION
    ) {
      return quota === 1 ? 'seat' : 'seats';
    }
    return quota === 1 ? 'license' : 'licenses';
  };

  const getQuotaText = (quota: number, feature = displaySubscription.feature) => (
    `${quota} ${getQuotaUnitLabel(feature, quota)}`
  );

  const getPlanName = (feature: Feature) => {
    switch (feature) {
      case Feature.TRUE_AUTOMATION:
        return intl.formatMessage({ id: 'page.plan.v2.true.automation.title' });
      case Feature.TRUE_PLATFORM_ONLY:
        return intl.formatMessage({ id: 'page.plan.v2.true.platform.title' });
      case Feature.TRUE_TEAM:
        return 'True Team';
      case Feature.TRUE_ENTERPRISE:
        return 'True Enterprise';
      default:
        return intl.formatMessage({
          id: `feature.${feature.toLowerCase()}.product.name`,
        });
    }
  };
  const truePlanName = getPlanName(displaySubscription.feature);
  const canManageAddOns = hasPaidSubscription && displaySubscription.feature === Feature.TRUE_TEAM;
  const canAddMoreAddOns = flags?.feTruePlatformAndTrueAutomationEnabled !== true;

  const getDaysRemaining = (subscription: Subscription) => (subscription.expiryDate
    ? Math.max(
      0,
      Math.floor(
        (new Date(subscription.expiryDate).getTime() - Date.now()) / MILLISECONDS_PER_DAY,
      ),
    )
    : 0);

  const getBillingCycleLabel = (subscription: Subscription) => {
    switch (subscription.billingInterval) {
      case PlanInterval.YEAR:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.year.short' });
      case PlanInterval.MONTH:
        return intl.formatMessage({ id: 'page.subscription.billing.interval.month.short' });
      default:
        return subscription.billingInterval ?? '-';
    }
  };

  const getSubscriptionTrackingPayload = (
    subscription: Subscription = displaySubscription,
    payload: TrackingPayload = {},
  ): TrackingPayload => ({
    field: 'current_subscription',
    object: Feature.toString(subscription.feature),
    product_id: getProductId(subscription.feature),
    subscription_id: subscription.id,
    recurly_subscription_id: subscription.recurlySubscriptionId,
    billing_interval: subscription.billingInterval,
    quantity: subscription.quota,
    group_quantity: displayQuota,
    paid_quantity: paidQuota,
    trial_quantity: trialQuota,
    source: subscription.source,
    auto_renew: autoRenew,
    ...payload,
  });

  const handleAddSeats = (subscription: Subscription = displaySubscription) => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(subscription, {
      action: 'add_seats',
    }));

    const prefix = getFeaturePrefix(subscription.feature);
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [PLAN_SUBSCRIPTION_ID_QUERY_PARAM]: subscription.id,
        [`${prefix}Quota`]: 1,
        [`${prefix}Cycle`]: subscription.billingInterval,
      }),
    );
  };

  const handleOpenReEnableDialog = () => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: 'open_reenable_auto_renew_dialog',
    }));
    setReEnableDialogOpen(true);
  };

  const handleToggleTrialDetails = () => {
    const nextShowTrialDetails = !showTrialDetails;
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      field: hasSubscriptionBreakdown ? 'subscription_breakdown' : 'trial_subscription',
      object: 'view_more',
      action: nextShowTrialDetails ? 'expand' : 'collapse',
    }));
    setShowTrialDetails(nextShowTrialDetails);
  };

  const trackAutoRenewDialogAction = (
    actionWhenOn: string,
    actionWhenOff: string,
  ) => {
    trackSubscriptionAction?.(getSubscriptionTrackingPayload(displaySubscription, {
      action: autoRenew ? actionWhenOn : actionWhenOff,
    }));
  };

  const handleReEnableAutoRenew = () => {
    if (!displaySubscription.recurlySubscriptionId) return;
    dispatch(fromSubscription.doToggleAutoRenew(displaySubscription.recurlySubscriptionId))
      .then(unwrapResult)
      .then(renewed => {
        setAutoRenew(renewed);
        setReEnableDialogOpen(false);
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'page.subscription.toggle.auto.renew.error' },
            { action: 'turn on' },
          ),
          { variant: 'error' },
        );
      });
  };

  const renderManagedByKatalon = () => (
    <Box>
      <span className={classes.managedBadge}>
        <FormattedMessage id="page.subscription.v2.managed.by.katalon" />
      </span>
      <Typography className={classes.managedText} mt={1}>
        <FormattedMessage id="page.subscription.v2.managed.by.katalon.desc" />
      </Typography>
    </Box>
  );

  const renderPaidSubscriptionDetail = (subscription: Subscription) => {
    const isExpandable = isSelfServePaidSubscription(subscription)
      && getFeaturePrefix(subscription.feature) !== 'Unknown';
    const isManaged = subscription.secondarySource === SecondarySource.DIRECT_SALE;
    const subscriptionAutoRenew = !!subscription.autoRenewEnabled && !subscription.canceledAt;

    return (
      <Box className={classes.detailRow} key={`${subscription.feature}-${subscription.id}`}>
        <Box className={classes.detailHeader}>
          <Box>
            <Typography className={classes.detailQuantity}>
              {getQuotaText(subscription.quota, subscription.feature)}
              {isManaged && (
                <span className={classes.managedBadge}>
                  <FormattedMessage id="page.subscription.v2.managed.by.katalon" />
                </span>
              )}
            </Typography>
            {isManaged && (
              <Typography className={classes.managedText} mt={0.75}>
                <FormattedMessage id="page.subscription.v2.managed.by.katalon.desc" />
              </Typography>
            )}
          </Box>
          {isExpandable && (
            <Button
              variant="contained"
              size="small"
              className={classes.addSeatsBtn}
              onClick={() => handleAddSeats(subscription)}
              sx={{
                borderRadius: 2,
                marginTop: 0,
              }}
            >
              <FormattedMessage id="page.subscription.v2.add.seats.btn" />
            </Button>
          )}
        </Box>

        <Box className={classes.renewalGrid}>
          <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
            <Typography className={classes.renewalLabel}>
              <FormattedMessage id="page.subscription.v2.next.renewal.label" />
            </Typography>
            <Typography className={classes.renewalDate}>
              {DateTime.formatDate(intl, subscription.expiryDate)}
            </Typography>
            <Typography className={classes.renewalDays}>
              <FormattedMessage
                id="page.subscription.v2.days.remaining"
                values={{ days: getDaysRemaining(subscription) }}
              />
            </Typography>
          </Box>

          <Divider
            id={`${subscription.feature}-${subscription.id}-detail-divider`}
            orientation="vertical"
            sx={{
              width: '1px',
              backgroundColor: '#E0E3EF',
              flexDirection: 'column',
            }}
            flexItem
          />

          <Box>
            <Typography className={classes.renewalLabel}>
              <FormattedMessage id="page.subscription.v2.billing.cycle.label" />
            </Typography>
            <Typography className={classes.billingCycleValue}>
              {getBillingCycleLabel(subscription)}
            </Typography>
            <Box sx={{ marginTop: 2 }}>
              <Typography className={classes.renewalLabel}>
                <FormattedMessage id="page.subscription.v2.auto.renew.label" />
              </Typography>
              <Typography
                className={classes.autoRenewStatus}
                sx={{ color: subscriptionAutoRenew ? '#17B26A' : '#808B9A' }}
              >
                {subscriptionAutoRenew
                  ? intl.formatMessage({ id: 'page.subscription.v2.auto.renew.on' })
                  : intl.formatMessage({ id: 'page.subscription.v2.auto.renew.off' })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Panel
        id={`${displaySubscription.feature}-upsell-panel`}
        sx={{
          width: '100%',
          minWidth: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        border
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          padding={theme => theme.spacing(3.5)}
          gap={3}
          minHeight={theme => theme.spacing(37)}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box className={classes.planNameRow}>
              <Typography className={classes.planName}>
                {truePlanName}
              </Typography>
              {isPrimaryTrial && (
                <span className={classes.trialTitleBadge}>
                  <FormattedMessage id="page.subscription.current.subscription.trial" />
                </span>
              )}
            </Box>
            <Typography className={classes.seatsLabel}>
              {hasPaidSubscription ? getQuotaText(paidQuota) : getQuotaText(trialQuota)}
              {hasPaidSubscription && hasTrialSubscription && (
                <span className={classes.trialQuantity}>
                  <FormattedMessage
                    id="page.subscription.v2.trial.quantity"
                    values={{ count: trialQuota }}
                  />
                </span>
              )}
            </Typography>
            {isManagedByKatalon && renderManagedByKatalon()}
          </Box>

          <Box className={classes.renewalGrid}>
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              minWidth={0}
            >
              <Typography className={classes.renewalLabel}>
                <FormattedMessage
                  id={isPrimaryTrial
                    ? 'page.subscription.v2.trial.ends.label'
                    : 'page.subscription.v2.next.renewal.label'}
                />
              </Typography>
              <Typography className={classes.renewalDate}>
                {DateTime.formatDate(intl, displaySubscription.expiryDate)}
              </Typography>
              <Typography className={classes.renewalDays}>
                <FormattedMessage
                  id="page.subscription.v2.days.remaining"
                  values={{ days: getDaysRemaining(displaySubscription) }}
                />
              </Typography>
            </Box>

            <Divider
              id={`${displaySubscription.feature}-upsell-panel-divider`}
              orientation="vertical"
              sx={theme => ({
                width: '1px',
                backgroundColor: theme.common.grey[700],
                flexDirection: 'column',
              })}
              flexItem
            />

            <Box>
              <Box>
                <Typography className={classes.renewalLabel}>
                  <FormattedMessage id="page.subscription.v2.billing.cycle.label" />
                </Typography>
                <Typography className={classes.billingCycleValue}>
                  {getBillingCycleLabel(displaySubscription)}
                </Typography>
              </Box>
              {showAutoRenew && (
                <Box
                  sx={{ marginTop: 2 }}
                >
                  <Typography className={classes.renewalLabel}>
                    <FormattedMessage id="page.subscription.v2.auto.renew.label" />
                  </Typography>
                  <Box className={classes.autoRenewRow}>
                    {displaySubscription.recurlySubscriptionId && (
                      <ToggleRenewal
                        productName={displaySubscription.feature.toLowerCase()}
                        autoRenewal={autoRenew}
                        recurlySubscriptionId={displaySubscription.recurlySubscriptionId}
                        setAutoRenew={setAutoRenew}
                        onOpenDialog={() => trackAutoRenewDialogAction(
                          'open_disable_auto_renew_dialog',
                          'open_enable_auto_renew_dialog',
                        )}
                        onCancelDialog={() => trackAutoRenewDialogAction(
                          'cancel_disable_auto_renew_dialog',
                          'cancel_enable_auto_renew_dialog',
                        )}
                        onConfirmDialog={() => trackAutoRenewDialogAction(
                          'confirm_disable_auto_renew',
                          'confirm_enable_auto_renew',
                        )}
                      />
                    )}
                    <Typography
                      className={classes.autoRenewStatus}
                      sx={{ color: autoRenew ? '#17B26A' : '#808B9A' }}
                    >
                      {autoRenew
                        ? intl.formatMessage({ id: 'page.subscription.v2.auto.renew.on' })
                        : intl.formatMessage({ id: 'page.subscription.v2.auto.renew.off' })}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {canAddSeats && (
            <Button
              variant="contained"
              size="small"
              className={classes.addSeatsBtn}
              onClick={() => handleAddSeats(displaySubscription)}
              sx={{
                borderRadius: 2,
              }}
            >
              <FormattedMessage id="page.subscription.v2.add.seats.btn" />
            </Button>
          )}
        </Box>

        {/* Auto-renew off warning banner */}
        {hasPaidSubscription
          && !autoRenew
          && displaySubscription.source === SubscriptionSource.RECURLY && (
          <Box className={classes.warningBanner}>
            <WarningAmberIcon sx={{ color: '#B7652A', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
            <Typography className={classes.warningText}>
              <strong style={{ color: '#B7652A' }}>
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.title" />
              </strong>
            </Typography>
            <Typography className={classes.warningText}>
              <FormattedMessage
                id="page.subscription.v2.auto.renew.off.warning.desc"
                values={{
                  date: (
                    <strong>{DateTime.formatDate(intl, displaySubscription.expiryDate)}</strong>
                  ),
                }}
              />
              <span
                className={classes.reEnableLink}
                role="button"
                tabIndex={0}
                onClick={handleOpenReEnableDialog}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpenReEnableDialog();
                }}
              >
                <FormattedMessage id="page.subscription.v2.auto.renew.off.warning.reenable" />
              </span>
            </Typography>
          </Box>
        )}

        {hasDetailPaidSubscriptions && (
          <Collapse in={showTrialDetails}>
            <Box className={classes.detailsSection}>
              {detailPaidSubscriptions.map(renderPaidSubscriptionDetail)}
            </Box>
          </Collapse>
        )}

        {hasTrialSubscription && trialSubscription && hasPaidSubscription && (
          <Collapse in={showTrialDetails}>
            <Box className={classes.trialSection}>
              <Box className={classes.trialHeader}>
                <span className={classes.trialBadge}>
                  <FormattedMessage id="page.subscription.current.subscription.trial" />
                </span>
                <Typography className={classes.trialHeaderQuantity}>
                  {getQuotaText(trialQuota, trialSubscription.feature)}
                </Typography>
              </Box>

              <Box className={classes.renewalGrid}>
                <Box display="flex" flexDirection="column">
                  <Typography className={classes.renewalLabel}>
                    <FormattedMessage id="page.subscription.v2.trial.ends.label" />
                  </Typography>
                  <Typography className={classes.renewalDate}>
                    {DateTime.formatDate(intl, trialSubscription.expiryDate)}
                  </Typography>
                  <Typography className={classes.renewalDays}>
                    <FormattedMessage
                      id="page.subscription.v2.days.remaining"
                      values={{ days: getDaysRemaining(trialSubscription) }}
                    />
                  </Typography>
                </Box>

                <Divider
                  id={`${displaySubscription.feature}-trial-panel-divider`}
                  orientation="vertical"
                  sx={{
                    width: '1px',
                    backgroundColor: '#E0E3EF',
                    flexDirection: 'column',
                  }}
                  flexItem
                />

                <Box>
                  <Typography className={classes.renewalLabel}>
                    <FormattedMessage id="page.subscription.v2.billing.cycle.label" />
                  </Typography>
                  <Typography className={classes.billingCycleValue}>
                    {getBillingCycleLabel(trialSubscription)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        )}

        {hasSubscriptionBreakdown && (
          <Button
            disableRipple
            className={classes.viewMoreRow}
            onClick={handleToggleTrialDetails}
          >
            {showTrialDetails
              ? <KeyboardArrowUpIcon className={classes.viewMoreIcon} fontSize="small" />
              : <KeyboardArrowDownIcon className={classes.viewMoreIcon} fontSize="small" />}
            <FormattedMessage id="page.subscription.v2.view.more.btn" />
          </Button>
        )}

        {/* Legacy True Team add-ons */}
        {canManageAddOns && (
          <>
            <Box className={classes.divider} />
            <AddOnsView
              key="addons-view"
              trueSubscription={displaySubscription}
              showActions={canAddMoreAddOns}
              trackSubscriptionAction={trackSubscriptionAction}
            />
          </>
        )}
      </Panel>
      {/* Re-enable renewal confirm dialog */}
      {hasPaidSubscription && displaySubscription.recurlySubscriptionId && (
        <ToggleRenewalConfirmDialog
          productName={displaySubscription.feature.toLowerCase()}
          autoRenew={autoRenew}
          open={reEnableDialogOpen}
          onClose={() => setReEnableDialogOpen(false)}
          toggleAutoRenew={handleReEnableAutoRenew}
          onCancel={() => trackSubscriptionAction?.(
            getSubscriptionTrackingPayload(displaySubscription, {
              action: 'cancel_reenable_auto_renew_dialog',
            }),
          )}
          onConfirm={() => trackSubscriptionAction?.(
            getSubscriptionTrackingPayload(displaySubscription, {
              action: 'confirm_reenable_auto_renew',
            }),
          )}
        />
      )}
    </>
  );
};
